import React, { forwardRef, useEffect } from 'react';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useFieldArray, Controller, useFormContext } from 'react-hook-form';
import { observer } from 'mobx-react';
import {
  DatePicker,
  FieldWrapperVerticalV2,
  TimePicker,
  useLocalization,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import IconLibrary from '../IconLibrary';
import PermissionButton, { PermissionIconButton } from '../PermissionButton';

const emptyField = {
  requestDate: null,
  afterHour: null,
  beforeHour: null,
};

const CustomerServiceRequests = forwardRef((props, ref) => {
  const { formatMessage } = useLocalization();
  const theme = useTheme();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const maxFields = props?.field?.max || 3;
  const minWidth = props?.minWidth || '600px';

  const { fields, append, remove } = useFieldArray({
    control,
    name: props.field.name,
  });

  useEffect(() => {
    if (fields.length === 0) {
      append({ ...emptyField });
    }
  }, [fields, fields.length, append]);

  if (
    props?.permission?.requestDate === 'READ' &&
    Array.isArray(fields) &&
    fields?.length === 0
  ) {
    return (
      <Typography fontWeight={500} variant="body2">
        No service request dates
      </Typography>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        mt: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.divider,
        borderRadius: 1,
        p: 1,
        minWidth: {
          md: minWidth,
        },
      }}>
      {fields.map((field, idx) => (
        <Grid2
          container
          spacing={1}
          alignItems="center"
          key={field.id}
          sx={{ mb: 1 }}>
          <Grid2 xs={4}>
            <Controller
              control={control}
              name={`${props.field.name}.${idx}.requestDate`}
              render={({ field: controllerField }) => {
                const fieldError = {
                  [controllerField?.name]:
                    errors[props.field.name]?.[idx]?.requestDate,
                };
                return (
                  <FieldWrapperVerticalV2
                    key={controllerField?.name}
                    field={{
                      name: controllerField?.name,
                      label: {
                        localeId: 'CUSTOMER_SERVICE_REQUEST_DATE_LABEL',
                        defaultMessage: 'Requested Date',
                      },
                      required: true,
                    }}
                    errors={fieldError}>
                    {/* Hack to avoid date field to render [Object object] for no reason */}
                    {idx === 0 && (
                      <TextField
                        style={{ display: 'none' }}
                        permission={props?.permission?.requestDate}
                      />
                    )}
                    <DatePicker
                      {...controllerField}
                      disablePast
                      required
                      value={controllerField.value}
                      onChange={date => {
                        controllerField.onChange(date);
                      }}
                      permission={props?.permission?.requestDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          InputLabelProps: {
                            shrink: true,
                            sx: { m: '0 !important' },
                          },
                        },
                      }}
                    />
                  </FieldWrapperVerticalV2>
                );
              }}
            />
          </Grid2>
          <Grid2 xs={3}>
            <Controller
              control={control}
              name={`${props.field.name}.${idx}.afterHour`}
              render={({ field: controllerField }) => {
                const fieldError = {
                  [controllerField?.name]:
                    errors[props.field.name]?.[idx]?.afterHour,
                };
                return (
                  <FieldWrapperVerticalV2
                    key={controllerField?.name}
                    field={{
                      name: controllerField?.name,
                      label: {
                        localeId: 'CUSTOMER_SERVICE_REQUEST_AFTER_HOUR_LABEL',
                        defaultMessage: 'After Hour',
                      },
                      required: false,
                    }}
                    errors={fieldError}>
                    <TimePicker
                      {...controllerField}
                      fullWidth
                      onChange={e => {
                        controllerField.onChange(e);
                      }}
                      permission={props?.permission?.afterHour}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputLabelProps: {
                            shrink: true,
                            sx: { m: '0 !important' },
                          },
                        },
                      }}
                    />
                  </FieldWrapperVerticalV2>
                );
              }}
            />
          </Grid2>
          <Grid2 xs={3}>
            <Controller
              control={control}
              name={`${props.field.name}.${idx}.beforeHour`}
              render={({ field: controllerField }) => {
                const fieldError = {
                  [controllerField?.name]:
                    errors[props.field.name]?.[idx]?.beforeHour,
                };
                return (
                  <FieldWrapperVerticalV2
                    key={controllerField?.name}
                    field={{
                      name: controllerField?.name,
                      label: {
                        localeId: 'CUSTOMER_SERVICE_REQUEST_BEFORE_HOUR_LABEL',
                        defaultMessage: 'Before Hour',
                      },
                      required: false,
                    }}
                    errors={fieldError}>
                    <TimePicker
                      {...controllerField}
                      fullWidth
                      onChange={e => {
                        controllerField.onChange(e);
                      }}
                      permission={props?.permission?.beforeHour}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputLabelProps: {
                            shrink: true,
                            sx: { m: '0 !important' },
                          },
                        },
                      }}
                    />
                  </FieldWrapperVerticalV2>
                );
              }}
            />
          </Grid2>
          {props?.permission?.requestDate === 'WRITE' && (
            <Grid2 xs={1} mt={3}>
              <PermissionIconButton
                permissionName="DELETE_SERVICE_REQUESTED_IN_JOB"
                size="small"
                onClick={() => {
                  remove(idx);
                }}
                color="error">
                <Delete />
              </PermissionIconButton>
            </Grid2>
          )}
        </Grid2>
      ))}
      {fields.length < maxFields &&
        props?.permission?.requestDate === 'WRITE' && (
          <PermissionButton
            permissionName="ADD_SERVICE_REQUESTED_IN_JOB"
            variant="text"
            startIcon={<IconLibrary.Add fontSize="inherit" />}
            onClick={() => {
              append({ ...emptyField });
            }}
            sx={{ mt: 0 }}>
            {formatMessage({
              id: 'SERVICE_REQUESTED_DATE_ADD_BUTTON',
              defaultMessage: 'Service Requested Date',
            })}
          </PermissionButton>
        )}
    </Box>
  );
});

export default observer(CustomerServiceRequests);
